import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";

export default function Login() {
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/";
  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") await login(email, password);
      else await signup(email, password, name || email.split("@")[0]);
      nav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    const redirect = window.location.origin + "/auth/callback";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">// Access the store</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mt-4 leading-[0.95]">
          {mode === "signin" ? "Welcome back." : "Claim your handle."}
        </h1>
        <p className="mt-6 text-white/70 max-w-md leading-relaxed">
          Sign in to buy limited drops or publish your own PDFs, music, podcasts, images and video.
        </p>
      </div>
      <div className="lg:col-span-6">
        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <button
            data-testid={AUTH.googleBtn}
            onClick={googleLogin}
            className="w-full border border-white/20 hover:border-white/50 font-mono uppercase tracking-[0.2em] text-xs py-3 flex items-center justify-center gap-3"
          >
            <span className="w-2 h-2 bg-[#CCFF00]" /> Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">or</div>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 block mb-2">Name</label>
                <input
                  data-testid={AUTH.nameInput}
                  className="brutal-input w-full px-3 py-3"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Rivera"
                  required
                />
              </div>
            )}
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 block mb-2">Email</label>
              <input
                data-testid={AUTH.emailInput}
                type="email"
                className="brutal-input w-full px-3 py-3"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 block mb-2">Password</label>
              <input
                data-testid={AUTH.passwordInput}
                type="password"
                className="brutal-input w-full px-3 py-3"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <button
              data-testid={AUTH.submitBtn}
              type="submit"
              disabled={busy}
              className="w-full bg-[#FF3366] text-white font-mono uppercase tracking-[0.2em] text-xs py-3 hover:bg-[#FF3366]/90 disabled:opacity-40"
            >
              {busy ? "Working…" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid={AUTH.toggleModeBtn}
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="font-mono text-xs text-white/60 hover:text-white uppercase tracking-[0.2em]"
            >
              {mode === "signin" ? "No account? Sign up →" : "Already have an account? Sign in →"}
            </button>
          </div>
        </div>
        <div className="mt-4 text-center">
          <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white/70">
            ← Back to store
          </Link>
        </div>
      </div>
    </div>
  );
}
